import { IconTrash } from 'assets/img';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LwButton, LwFormTime } from 'redesign';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { Box, Grid, styled } from '@mui/material';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { isOverlappingSegment } from '../utils/is-overlapping-segment';

type TimeslotProps = {
  index: number;
  handleDelete: (index: number) => void;
};
export const Timeslot = ({ index, handleDelete }: TimeslotProps) => {
  const { t } = useTranslation('all-platforms-common');
  const { control, watch, setError, clearErrors } = useFormContext<TimeSlotFormContext>();
  const timeslots = watch('availability');

  //I don't like this at all, but there's something extremely off about how array values play with the native validation
  const validateTimeSlot = (slot: SegmentsForEdit) => {
    const overlappingSlot = isOverlappingSegment(slot, timeslots);
    const isInternallyInvalid =
      DateTime.fromISO(slot.utcStartDateTime) >= DateTime.fromISO(slot.utcEndDateTime);

    const startField = `availability.${index}.utcStartDateTime` as const;
    const endField = `availability.${index}.utcEndDateTime` as const;
    const overlappingErrorMessage = t('edit_availability.timeslot_overlap', {
      ns: 'all-platforms-common',
    });
    const internallyInvalidErrorMessage = t('edit_availability.timeslot_invalid', {
      ns: 'all-platforms-common',
    });
    if (isInternallyInvalid) {
      const error = { type: 'manual', message: internallyInvalidErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else if (overlappingSlot) {
      const error = { type: 'manual', message: overlappingErrorMessage };
      setError(startField, error);
      setError(endField, error);
    } else {
      clearErrors([startField, endField]);
    }
  };

  const isDeleteButtonDisabled = useMemo(() => {
    return timeslots.length <= 1;
  }, [timeslots.length]);

  useEffect(() => {
    validateTimeSlot(timeslots[index]);
    //This dependency needs to be stringified like this because the logic contained within validateTimeSlot
    //relies on the properties of every member of the timeslots array
    //forgive me
  }, [JSON.stringify(timeslots)]);

  return (
    <StyledContainer>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcStartDateTime`}
            label={t('edit_availability.start_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={6}>
          <LwFormTime
            name={`availability.${index}.utcEndDateTime`}
            label={t('edit_availability.end_time', { ns: 'all-platforms-common' })}
            control={control}
            rules={{ required: true }}
          />
        </Grid>
      </Grid>
      <StyledDeleteButton
        startIcon={<IconTrash />}
        color="tertiary"
        onClick={() => handleDelete(index)}
        disabled={isDeleteButtonDisabled}
      >
        {t('edit_availability.delete_timeslot')}
      </StyledDeleteButton>
    </StyledContainer>
  );
};

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const StyledDeleteButton = styled(LwButton)(({ theme }) => ({
  marginLeft: 'auto',
  alignContent: 'flex-end',
}));
